import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
  HOLD_BUTTON_ID,
  NEW_ORDER_BUTTON_ID,
  REMOVE_HOLD_BUTTON_ID,
  RESHIP_BUTTON_ID,
  RESTORE_SHIPMENT_BUTTON_ID,
} from './orders-grid-button-ids';
import AllocationMenuItems from '../../../components/allocate-menu/allocation-menu-items';
import { OrdersGridAllocateMenu } from '../../../components/allocate-menu/orders-grid-allocate-menu';
import OtherActionButton from '../../../components/grid-header/other-action-button';
import RefreshTrackingButton from '../../../components/grid-header/refresh-tracking-button';
import { CancelFulfillmentButton } from '../../../components/orders-grid/cancel-fulfillment-button';
import { OrdersGridPrintDocumentButton } from '../../../components/orders-grid/print-document-button';
import { QuickshipButton } from '../../../components/orders-grid/quickship-button';
import TagButton from '../../../components/orders-tag-button';
import { AssignToOrders } from '../../../containers/assign-to-button-container/assign-to-orders';
import { OrdersGridBulkUpdateMenuContainer } from '../../../containers/bulk-update/orders-grid-bulk-update-menu-container';
import { BulkUpdateButtonContainer } from '../../../containers/bulk-update/orders-grid-bulk-update-menu-container/bulk-update-button-container';
import { CreateLabelButtonContainer } from '../../../containers/create-label-button/orders-bulk';
import { MenuItemsContainer } from '../../../containers/orders-other-action-menu-container/menu-items-container';
import { OrdersReusableGridButtonContainer } from '../../../containers/orders-reusable-grid-button-container';
import { BulkReshipButton } from '../../../orders/reship/bulk-reship-button';
import { ReadyToPickButton } from '../../orders-batches/ready-to-pick/ready-to-pick-button';
import {
  ADVANCED_SEARCH_ID,
  ALL_ORDERS_SEARCH_RESULT_ID,
  AWAITING_PAYMENT_ID,
  AWAITING_SHIPMENT_ID,
  CANCELLED_ID,
  ON_HOLD_ID,
  PENDING_FULFILLMENT_ID,
  PENDING_PICKUP_ID,
  REJECTED_FULFILLMENT_ID,
  SHIPPED_ID,
} from '../orders-page-view-constants';

const messages = defineMessages({
  otherActions: {
    description: 'actionBar.otherActions',
    defaultMessage: 'Other Actions',
    id: 'jXLJts',
  },
  hold: {
    description: 'actionBar.hold',
    defaultMessage: 'Hold',
    id: 'm98vV2',
  },
  bulkUpdate: {
    description: 'actionBar.bulkUpdate',
    defaultMessage: 'Bulk Update',
    id: 'WY261q',
  },
  allocate: {
    description: 'actionBar.allocate',
    defaultMessage: 'Allocate',
    id: 'YAuAx+',
  },
  cancelFulfillment: {
    description: 'orders page action bar cancel fulfillment button',
    defaultMessage: 'Cancel Fulfillment',
    id: '7b7FLX',
  },
  readyToPickButton: {
    description: 'Order Grid Ready to Pick Button',
    defaultMessage: 'Ready to Pick',
    id: 'Ph3ylG',
  },
  refreshTracking: {
    description: 'Order Grid: Refresh Tracking Action Button Text',
    defaultMessage: 'Update Tracking',
    id: 'nZkLzr',
  },
  restore: {
    description: 'orderReusableGridButton.restore',
    defaultMessage: 'Restore',
    id: 'SGO4Xh',
  },
});

const buttons = {
  allocate: {
    type: OrdersGridAllocateMenu,
    menu: AllocationMenuItems,
    overflowName: <FormattedMessage {...messages.allocate} />,
    id: 'allocate',
  },
  assignOrder: {
    type: AssignToOrders,
    requiredRender: true,
  },
  bulkUpdate: {
    type: BulkUpdateButtonContainer,
    menu: OrdersGridBulkUpdateMenuContainer,
    overflowName: <FormattedMessage {...messages.bulkUpdate} />,
    id: 'bulk-update',
  },
  cancelFulfillment: {
    type: CancelFulfillmentButton,
    overflowName: <FormattedMessage {...messages.cancelFulfillment} />,
    id: 'cancel-fulfillment',
    theme: 'tertiary',
  },
  createLabel: {
    type: CreateLabelButtonContainer,
    requiredRender: true,
    theme: 'primary',
  },
  getRate: {
    type: OrdersReusableGridButtonContainer,
    id: 'Get Rate',
    requiredRender: true,
  },
  hold: {
    type: OrdersReusableGridButtonContainer,
    id: HOLD_BUTTON_ID,
    overflowName: <FormattedMessage {...messages.hold} />,
    requiredRender: true,
    theme: 'tertialy-revision2',
  },
  markAsPaid: {
    type: OrdersReusableGridButtonContainer,
    id: 'Mark as Paid',
  },
  markAsReadyToPick: {
    type: ReadyToPickButton,
    id: 'Mark as Ready to Pick',
    requiredRender: true,
  },
  newOrder: {
    type: OrdersReusableGridButtonContainer,
    id: NEW_ORDER_BUTTON_ID,
    requiredRender: true,
  },
  otherActions: {
    overflowName: <FormattedMessage {...messages.otherActions} />,
    id: 'other-actions',
    type: OtherActionButton,
    menu: MenuItemsContainer,
  },
  print: {
    type: OrdersGridPrintDocumentButton,
    requiredRender: true,
  },
  removeHold: {
    type: OrdersReusableGridButtonContainer,
    id: REMOVE_HOLD_BUTTON_ID,
  },
  restore: {
    type: OrdersReusableGridButtonContainer,
    overflowName: <FormattedMessage {...messages.restore} />,
    id: 'Restore',
  },
  tag: {
    type: TagButton,
    requiredRender: true,
  },
  quickship: {
    type: QuickshipButton,
    requiredRender: true,
  },
  refreshTracking: {
    type: RefreshTrackingButton,
    id: 'refreshTracking',
    overflowName: <FormattedMessage {...messages.refreshTracking} />,
  },
  restoreShipments: {
    type: OrdersReusableGridButtonContainer,
    overflowName: <FormattedMessage {...messages.restore} />,
    id: RESTORE_SHIPMENT_BUTTON_ID,
    requiredRender: true,
  },
  reship: {
    type: BulkReshipButton,
    overflowName: <FormattedMessage {...messages.reshipButton} />,
    id: RESHIP_BUTTON_ID,
    requiredRender: true,
  },
  markAsShipped: {
    type: OrdersReusableGridButtonContainer,
    id: 'Mark as Shipped',
    requiredRender: true,
  },
};

const arrangeButtons = (buttonTypes) => {
  const buttonTypesLookupHash = buttonTypes.reduce((acc, buttonType) => {
    return {
      ...acc,
      [buttonType]: true,
    };
  }, {});
  return [
    'createLabel',
    'quickship',
    'cancelFulfillment',
    'reship',
    'restoreShipments',
    'getRate',
    'markAsPaid',
    'removeHold',
    'restore',
    'print',
    'markAsReadyToPick',
    'markAsShipped',
    'hold',
    'assignOrder',
    'bulkUpdate',
    'allocate',
    'tag',
    'newOrder',
    'refreshTracking',
    'otherActions',
  ].reduce((acc, buttonType) => {
    if (buttonTypesLookupHash[buttonType]) {
      return [...acc, buttons[buttonType]];
    }
    return acc;
  }, []);
};

export const getOrdersGridAvailableButtons = () => ({
  [ADVANCED_SEARCH_ID]: arrangeButtons([
    'createLabel',
    'quickship',
    'getRate',
    'print',
    'markAsReadyToPick',
    'assignOrder',
    'removeHold',
    'hold',
    'bulkUpdate',
    'allocate',
    'tag',
    'newOrder',
    'otherActions',
  ]),
  [ALL_ORDERS_SEARCH_RESULT_ID]: arrangeButtons([
    'createLabel',
    'quickship',
    'getRate',
    'print',
    'markAsReadyToPick',
    'assignOrder',
    'removeHold',
    'hold',
    'bulkUpdate',
    'allocate',
    'tag',
    'newOrder',
    'otherActions',
  ]),
  [AWAITING_SHIPMENT_ID]: arrangeButtons([
    'createLabel',
    'quickship',
    'getRate',
    'print',
    'markAsReadyToPick',
    'assignOrder',
    'hold',
    'bulkUpdate',
    'allocate',
    'tag',
    'newOrder',
    'otherActions',
  ]),
  [PENDING_FULFILLMENT_ID]: arrangeButtons(['tag', 'newOrder', 'cancelFulfillment', 'otherActions']),
  [REJECTED_FULFILLMENT_ID]: arrangeButtons([
    'createLabel',
    'quickship',
    'getRate',
    'print',
    'restoreShipments',
    'assignOrder',
    'hold',
    'bulkUpdate',
    'allocate',
    'tag',
    'cancelFulfillment',
    'otherActions',
  ]),
  [PENDING_PICKUP_ID]: arrangeButtons(['print', 'newOrder', 'tag', 'assignOrder', 'refreshTracking']),
  [AWAITING_PAYMENT_ID]: arrangeButtons(['markAsPaid', 'print', 'assignOrder', 'hold', 'bulkUpdate', 'tag', 'newOrder', 'otherActions']),
  [ON_HOLD_ID]: arrangeButtons(['createLabel', 'removeHold', 'print', 'assignOrder', 'bulkUpdate', 'tag', 'newOrder', 'otherActions']),
  [SHIPPED_ID]: arrangeButtons(['reship', 'print', 'assignOrder', 'bulkUpdate', 'tag', 'newOrder', 'restoreShipments', 'otherActions']),
  [CANCELLED_ID]: arrangeButtons(['restore', 'print', 'assignOrder', 'bulkUpdate', 'tag', 'newOrder', 'otherActions']),
});
